import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Drawer, Input } from 'antd';
import { T } from 'react-translator-component';

import { BlockSpacer, CommentBody, FieldLabel, SpacedAlert, TopSpacedButton } from './styled';
import { CommentDate, TicketFields, TicketHistory } from './TicketContent';

import { getTicket, sendComment } from '../../../hooks/jira';
import { TicketDetail } from '../../../types/responses/Jira';

// Os tipos do antd 4.10 com @types/react 18 nao declaram `children` em Drawer.
const AntDrawer = Drawer as React.FC<React.ComponentProps<typeof Drawer> & { children?: React.ReactNode }>;

const POLL_MS = 120000;

interface Props {
  ticketKey: string | null;
  onClose: () => void;
}

// Todo texto vindo do Jira (titulo, campos, autores, comentarios) entra como texto React:
// nunca HTML. pre-wrap preserva as quebras de linha.
const TicketDrawer = ({ ticketKey, onClose }: Props) => {
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  // `session` muda a cada troca/fechamento/desmontagem; `loadSeq` a cada busca iniciada.
  const session = useRef(0);
  const loadSeq = useRef(0);
  const sendingRef = useRef(false);

  const load = useCallback(async () => {
    if (!ticketKey) return;
    const current = session.current;
    loadSeq.current += 1;
    const call = loadSeq.current;
    try {
      const loaded = await getTicket(ticketKey);
      if (current !== session.current || call !== loadSeq.current) return;
      setDetail(loaded);
      setError('');
    } catch (e: any) {
      if (current !== session.current || call !== loadSeq.current) return;
      setError(e?.response?.status === 403 ? 'You do not have access to this area' : 'Could not load the ticket');
    }
  }, [ticketKey]);

  useEffect(() => {
    session.current += 1;
    sendingRef.current = false;
    setSending(false);
    setDetail(null);
    setReply('');
    setError('');
    if (!ticketKey) return undefined;
    load();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => {
      clearInterval(id);
      session.current += 1;
    };
  }, [ticketKey, load]);

  const send = async () => {
    const text = reply.trim();
    if (!ticketKey || !text || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setError('');
    const current = session.current;
    try {
      await sendComment(ticketKey, text);
      if (current !== session.current) return;
      setReply('');
      await load();
    } catch {
      if (current === session.current) setError('Could not send the message');
    } finally {
      if (current === session.current) {
        sendingRef.current = false;
        setSending(false);
      }
    }
  };

  return (
    <AntDrawer
      visible={!!ticketKey}
      onClose={onClose}
      width={520}
      title={detail?.summary.title || ticketKey || ''}
      destroyOnClose
    >
      {error && (
        <SpacedAlert
          type="error"
          message={T(error)}
          action={
            <Button size="small" onClick={load}>
              {T('Try again')}
            </Button>
          }
        />
      )}
      {detail && (
        <>
          <p>
            <strong>{detail.summary.key}</strong> · {detail.summary.statusLabel}
          </p>
          <TicketFields
            fields={detail.fields}
            fieldLabels={detail.fieldLabels}
            childTenantName={detail.summary.childTenantName}
          />
          <TicketHistory history={detail.history} />
          <h4>{T('Messages')}</h4>
          {detail.comments.map((c) => (
            <BlockSpacer key={c.id}>
              <strong>{c.author}</strong>
              <CommentDate iso={c.createdAt} />
              <CommentBody>{c.body}</CommentBody>
            </BlockSpacer>
          ))}
          <FieldLabel htmlFor="ticket-reply">{T('Write a reply')}</FieldLabel>
          <Input.TextArea
            id="ticket-reply"
            rows={3}
            value={reply}
            placeholder={T('Write a reply')}
            onChange={(e) => setReply(e.target.value)}
          />
          <TopSpacedButton type="primary" loading={sending} onClick={send}>
            {T('Send')}
          </TopSpacedButton>
        </>
      )}
    </AntDrawer>
  );
};

export default TicketDrawer;
